/**
 * Editeur HTML/CSS a onglets.
 *
 * Remplace deux `<textarea>` bruts par CodeMirror, deja utilise ailleurs dans
 * la plateforme (voir `components/IDE/ide`). Apport principal au-dela du
 * confort : l'insertion depuis les panneaux d'aide passe desormais par une
 * transaction de l'editeur. L'implementation precedente ecrivait directement
 * dans le DOM (`textarea.value = ...`) avant de prevenir React, ce qui faisait
 * diverger les deux et perdait la position du curseur.
 */

import React, { useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { Button, Tabs } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { html as htmlLanguage } from '@codemirror/lang-html';
import { css as cssLanguage } from '@codemirror/lang-css';
import { EditorView } from '@codemirror/view';
import { vscodeDark } from '@uiw/codemirror-theme-vscode';
import { HtmlCssHelpItem, HtmlCssWorkspace } from '../core/types';

export type EditorTab = 'html' | 'css';

export interface HtmlCssEditorPaneRef {
  /** Insere un extrait a la position du curseur de l'onglet indique. */
  insertSnippet: (tab: EditorTab, snippet: string) => void;
}

interface HtmlCssEditorPaneProps {
  workspace: HtmlCssWorkspace;
  onHtmlChange: (html: string) => void;
  onCssChange: (css: string) => void;
  readOnly?: boolean;
  activeTab: EditorTab;
  onTabChange: (tab: EditorTab) => void;
  /** Listes d'aide ; un bouton n'est rendu que si sa liste est non vide. */
  htmlHelpItems: HtmlCssHelpItem[];
  cssHelpItems: HtmlCssHelpItem[];
  selectorHelpItems?: HtmlCssHelpItem[];
  onOpenHtmlHelp: () => void;
  onOpenCssHelp: () => void;
  onOpenSelectorHelp: () => void;
}

/** L'editeur occupe toute la hauteur disponible du panneau. */
const fillHeight = EditorView.theme({
  '&': { height: '100%' },
  '.cm-scroller': { overflow: 'auto' },
});

const HtmlCssEditorPane = React.forwardRef<HtmlCssEditorPaneRef, HtmlCssEditorPaneProps>(
  (
    {
      workspace,
      onHtmlChange,
      onCssChange,
      readOnly = false,
      activeTab,
      onTabChange,
      htmlHelpItems,
      cssHelpItems,
      selectorHelpItems,
      onOpenHtmlHelp,
      onOpenCssHelp,
      onOpenSelectorHelp,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const htmlEditorRef = useRef<ReactCodeMirrorRef>(null);
    const cssEditorRef = useRef<ReactCodeMirrorRef>(null);

    const htmlExtensions = useMemo(() => [htmlLanguage(), fillHeight], []);
    const cssExtensions = useMemo(() => [cssLanguage(), fillHeight], []);

    /**
     * Insere un extrait via l'API de l'editeur.
     *
     * Le curseur est place apres le texte insere et l'editeur reprend le focus,
     * pour que l'eleve puisse continuer a taper sans repointer la souris.
     */
    const insertSnippet = useCallback((tab: EditorTab, snippet: string) => {
      const editor = tab === 'html' ? htmlEditorRef.current : cssEditorRef.current;
      const view = editor?.view;
      if (!view) return;

      const { from, to } = view.state.selection.main;
      // Un saut de ligne n'est ajoute que si l'insertion ne demarre pas deja en
      // debut de ligne, pour ne pas creuser d'interligne superflu.
      const atLineStart = from === view.state.doc.lineAt(from).from;
      const text = atLineStart ? snippet : `\n${snippet}`;

      view.dispatch({
        changes: { from, to, insert: text },
        selection: { anchor: from + text.length },
        scrollIntoView: true,
      });
      view.focus();
    }, []);

    useImperativeHandle(ref, () => ({ insertSnippet }), [insertSnippet]);

    /** Barre d'actions d'un onglet ; masquee si aucune aide n'est configuree. */
    const renderHelpBar = (buttons: React.ReactNode[]) => {
      const visible = buttons.filter(Boolean);
      if (visible.length === 0) return null;
      return <div className="htmlcss-editor-toolbar">{visible}</div>;
    };

    const items = [
      {
        key: 'html',
        label: t('htmlCss.editor.htmlTab'),
        children: (
          <div className="htmlcss-editor-pane">
            {renderHelpBar([
              htmlHelpItems.length > 0 && (
                <Button
                  key="html-help"
                  className="htmlcss-help-btn"
                  icon={<QuestionCircleOutlined />}
                  onClick={onOpenHtmlHelp}
                >
                  {t('htmlCss.editor.htmlHelp')}
                </Button>
              ),
            ])}
            <div className="htmlcss-editor-surface">
              <CodeMirror
                ref={htmlEditorRef}
                value={workspace.html}
                onChange={onHtmlChange}
                readOnly={readOnly}
                theme={vscodeDark}
                extensions={htmlExtensions}
                height="100%"
                basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true }}
              />
            </div>
          </div>
        ),
      },
      {
        key: 'css',
        label: t('htmlCss.editor.cssTab'),
        children: (
          <div className="htmlcss-editor-pane">
            {renderHelpBar([
              cssHelpItems.length > 0 && (
                <Button
                  key="css-help"
                  className="htmlcss-help-btn"
                  icon={<QuestionCircleOutlined />}
                  onClick={onOpenCssHelp}
                >
                  {t('htmlCss.editor.cssHelp')}
                </Button>
              ),
              // Le bouton d'aide aux selecteurs n'apparait que si l'enseignant
              // a configure cette liste : il n'impose plus un contenu fige et
              // hors programme a toutes les lecons.
              selectorHelpItems && selectorHelpItems.length > 0 && (
                <Button
                  key="selector-help"
                  className="htmlcss-help-btn"
                  icon={<QuestionCircleOutlined />}
                  onClick={onOpenSelectorHelp}
                >
                  {t('htmlCss.editor.selectorHelp')}
                </Button>
              ),
            ])}
            <div className="htmlcss-editor-surface">
              <CodeMirror
                ref={cssEditorRef}
                value={workspace.css}
                onChange={onCssChange}
                readOnly={readOnly}
                theme={vscodeDark}
                extensions={cssExtensions}
                height="100%"
                basicSetup={{ lineNumbers: true, foldGutter: false, highlightActiveLine: true }}
              />
            </div>
          </div>
        ),
      },
    ];

    return (
      <Tabs
        className="htmlcss-editor-tabs"
        activeKey={activeTab}
        onChange={key => onTabChange(key as EditorTab)}
        items={items}
        tabBarStyle={{ marginBottom: 0 }}
      />
    );
  }
);

HtmlCssEditorPane.displayName = 'HtmlCssEditorPane';

export default HtmlCssEditorPane;
